import * as Yup from "yup";

import {
  StatusPageTimeFormat,
  StatusPageType,
  StatusPageVisibilityLevel,
  UptimeCalculationType,
} from "@/lib/types/statuspages.ts";
import {StatusPageGeneralFormValues} from "@/pages/statuspages/tabs/general/StatusPageGeneralTab.types.ts";

export const StatusPageGeneralSchema = Yup.object({
  name: Yup.string().required("This field cannot be blank."),
  page_type: Yup.mixed<StatusPageType>().oneOf(Object.values(StatusPageType)).required(),
  description: Yup.string().defined(),
  timezone: Yup.string().defined(),
  time_format: Yup.string().defined(),

  show_status_tab: Yup.boolean(),
  default_status_date_range: Yup.number().when("show_status_tab", {
    is: true,
    then: () =>
      Yup.number()
        .min(1, "Must be 1 or more")
        .max(90, "Must be 90 or less")
        .required()
        .typeError("A valid integer is required."),
  }),
  show_active_incidents: Yup.boolean().optional(),
  show_component_response_time: Yup.boolean().optional(),
  hide_empty_tabs_status: Yup.boolean(),

  show_history_tab: Yup.boolean(),
  default_history_date_range: Yup.number().when("show_history_tab", {
    is: true,
    then: () =>
      Yup.number()
        .min(1, "Must be 1 or more")
        .max(90, "Must be 90 or less")
        .required()
        .typeError("A valid integer is required."),
  }),
  max_visible_days: Yup.number()
    .nullable()
    .transform((val, orig) => (orig == "" ? null : val))
    .min(1, () => `Must be greater than zero`)
    .typeError(() => `Must be an integer`),
  uptime_calculation_type: Yup.string().when("show_history_tab", {
    is: true,
    then: () => Yup.string().required(),
  }),
  allow_pdf_report: Yup.boolean().optional(),
  show_history_snake: Yup.boolean().optional(),
  show_summary_metrics: Yup.boolean().optional(),
  show_component_history: Yup.boolean().optional(),
  show_past_incidents: Yup.boolean().optional(),
  show_overall_status: Yup.boolean().optional(),
  show_branding_footer: Yup.boolean().optional(),
  hide_empty_tabs_history: Yup.boolean(),

  // Visibility & access controls (previously on the Privacy tab)
  visibility_level: Yup.string().defined(),
  slug: Yup.string()
    .notRequired()
    .when("visibility_level", {
      is: (value: StatusPageVisibilityLevel) => value !== StatusPageVisibilityLevel.UPTIME_USERS,
      then: () =>
        Yup.string()
          .min(3, "This field must be at least 3 characters long.")
          .required("This field is required to publish your status page."),
    }),
  cname: Yup.string().optional().nullable(),
  password_protection_enabled: Yup.boolean().default(false).optional(),
  allow_search_indexing: Yup.boolean().default(true).optional(),
  allow_drill_down: Yup.boolean().default(true).optional(),
  auth_username: Yup.string().defined(),
  auth_password: Yup.string().defined(),
  max_visible_component_days: Yup.number()
    .nullable()
    .transform((val, orig) => (orig == "" ? null : val))
    .min(1, () => `Must be greater than zero`)
    .typeError(() => `Must be an integer`),
  google_analytics_code: Yup.string().optional(),
});

export const StatusPageGeneralDefaultValues: StatusPageGeneralFormValues = {
  name: "",
  page_type: StatusPageType.PUBLIC,
  description: "",
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  time_format: StatusPageTimeFormat.TWENTY_FOUR_HOUR,

  show_status_tab: true,
  default_status_date_range: 7,
  show_active_incidents: true,
  show_component_response_time: true,
  hide_empty_tabs_status: false,

  show_history_tab: true,
  hide_empty_tabs_history: false,

  default_history_date_range: 7,
  max_visible_days: null,
  uptime_calculation_type: UptimeCalculationType.BY_INCIDENTS,

  allow_pdf_report: true,
  show_history_snake: true,
  show_summary_metrics: true,
  show_component_history: true,
  show_past_incidents: true,
  show_overall_status: true,
  show_branding_footer: true,

  visibility_level: StatusPageVisibilityLevel.PUBLIC,
  slug: "",
  cname: "",
  password_protection_enabled: false,
  auth_username: "",
  auth_password: "",
  allow_search_indexing: true,
  allow_drill_down: true,
  max_visible_component_days: null,
  google_analytics_code: "",
};
